import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Filter, Mail, Eye, EyeOff, Loader2, Search, ArrowRight, RefreshCw, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiCall } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";


interface MonitoredUser {
    id: number;
    email: string;
    label_name: string;
    is_active: boolean;
    watch_expiration: string | null;
    history_id: string | null;
    filter_senders_count?: number;
}

interface FilterSender {
    id: number;
    sender_email: string;
    description: string | null;
    is_active: boolean;
    monitored_user_id: number;
}

const EmailFilterPage: React.FC = () => {
    const { token } = useAuth();
    const [monitoredUsers, setMonitoredUsers] = useState<MonitoredUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<MonitoredUser | null>(null);
    const [filterSenders, setFilterSenders] = useState<FilterSender[]>([]);
    const [loading, setLoading] = useState(false);

    // Dialog states
    const [isAddUserOpen, setIsAddUserOpen] = useState(false);
    const [isAddSenderOpen, setIsAddSenderOpen] = useState(false);
    const [isWatchLoading, setIsWatchLoading] = useState<number | null>(null);

    // Form states
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newLabelName, setNewLabelName] = useState("Priority Leads");

    const [newSenderEmail, setNewSenderEmail] = useState("");
    const [newSenderDescription, setNewSenderDescription] = useState("");

    useEffect(() => {
        fetchMonitoredUsers();
    }, [token]);

    useEffect(() => {
        if (selectedUser) {
            fetchFilterSenders(selectedUser.id);
        } else {
            setFilterSenders([]);
        }
    }, [selectedUser, token]);

    const fetchMonitoredUsers = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response: any = await apiCall(token, '/email-filter/users');
            setMonitoredUsers(response.data);
        } catch (error) {
            console.error("Error fetching monitored users:", error);
            toast.error("Erreur lors du chargement des utilisateurs suivis");
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterSenders = async (userId: number) => {
        if (!token) return;
        try {
            const response: any = await apiCall(token, `/email-filter/senders?monitored_user_id=${userId}`);
            setFilterSenders(response.data);
        } catch (error) {
            console.error("Error fetching filter senders:", error);
            toast.error("Erreur lors du chargement de la liste de filtrage");
        }
    };

    const handleAddUser = async () => {
        if (!token) return;
        try {
            await apiCall(token, '/email-filter/users', 'POST', {
                email: newUserEmail,
                label_name: newLabelName
            });
            toast.success("Utilisateur suivi ajouté avec succès");
            setIsAddUserOpen(false);
            setNewUserEmail("");
            setNewLabelName("Priority Leads");
            fetchMonitoredUsers();
        } catch (error: any) {
            console.error("Error adding monitored user:", error);
            toast.error(error.message || "Erreur lors de l'ajout");
        }
    };

    const handleDeleteUser = async (id: number) => {
        if (!token || !confirm("Êtes-vous sûr de vouloir arrêter le suivi pour cet utilisateur ?")) return;
        try {
            await apiCall(token, `/email-filter/users/${id}`, 'DELETE');
            toast.success("Utilisateur retiré du suivi");
            if (selectedUser?.id === id) setSelectedUser(null);
            fetchMonitoredUsers();
        } catch (error) {
            console.error("Error deleting user:", error);
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleSetupWatch = async (user: MonitoredUser) => {
        if (!token) return;
        setIsWatchLoading(user.id);
        try {
            await apiCall(token, `/email-filter/users/${user.id}/setup-watch`, 'POST');
            toast.success(`Surveillance Gmail activée pour ${user.email}`);
            fetchMonitoredUsers();
        } catch (error: any) {
            console.error("Error setting up watch:", error);
            toast.error("Échec de l'activation : " + (error.response?.data?.message || "Erreur inconnue"));
        } finally {
            setIsWatchLoading(null);
        }
    };

    const handleAddSender = async () => {
        if (!token || !selectedUser) return;
        try {
            await apiCall(token, '/email-filter/senders', 'POST', {
                monitored_user_id: selectedUser.id,
                sender_email: newSenderEmail,
                description: newSenderDescription
            });
            toast.success("Expéditeur ajouté à la liste de filtrage");
            setIsAddSenderOpen(false);
            setNewSenderEmail("");
            setNewSenderDescription("");
            fetchFilterSenders(selectedUser.id);
            fetchMonitoredUsers(); // Update counts
        } catch (error: any) {
            console.error("Error adding sender:", error);
            toast.error(error.message || "Erreur lors de l'ajout");
        }
    };

    const handleDeleteSender = async (id: number) => {
        if (!token || !selectedUser) return;
        try {
            await apiCall(token, `/email-filter/senders/${id}`, 'DELETE');
            toast.success("Expéditeur retiré de la liste");
            fetchFilterSenders(selectedUser.id);
            fetchMonitoredUsers(); // Update counts
        } catch (error) {
            console.error("Error deleting sender:", error);
            toast.error("Erreur lors de la suppression");
        }
    };

    return (
        <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-2xl">
                            <Filter className="h-6 w-6 text-primary" />
                        </div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Filtrage Emails</h1>
                    </div>
                    <p className="text-slate-500 font-medium ml-14">Gérez le filtrage automatique des emails Gmail entrants.</p>
                </div>
                <Button
                    onClick={() => setIsAddUserOpen(true)}
                    className="h-12 px-6 rounded-2xl bg-primary shadow-xl shadow-primary/20 text-white font-bold hover:scale-105 active:scale-95 transition-all text-xs uppercase tracking-widest"
                >
                    <Plus className="mr-2 h-4 w-4" /> Nouvel Utilisateur Suivi
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Users List */}
                <Card className="col-span-1 border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl h-fit">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-black uppercase tracking-tight">Utilisateurs Suivis</CardTitle>
                        <CardDescription>Sélectionnez un utilisateur pour gérer sa liste.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-slate-100">
                            {loading ? (
                                <div className="p-8 flex justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : monitoredUsers.length === 0 ? (
                                <div className="p-8 text-center text-slate-500 text-sm font-medium">
                                    Aucun utilisateur configuré.
                                </div>
                            ) : (
                                monitoredUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className={cn(
                                            "p-4 hover:bg-slate-50 transition-all cursor-pointer group border-l-4",
                                            selectedUser?.id === user.id ? "bg-slate-50 border-l-primary" : "border-l-transparent"
                                        )}
                                        onClick={() => setSelectedUser(user)}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-bold text-slate-900 group-hover:text-primary transition-colors flex items-center gap-2">
                                                {user.email}
                                            </div>
                                            <Badge variant={user.watch_expiration && new Date(user.watch_expiration) > new Date() ? "default" : "destructive"}
                                                className={cn("rounded-lg text-[10px]",
                                                    user.watch_expiration && new Date(user.watch_expiration) > new Date() ? "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20" : "bg-red-500/10 text-red-600 hover:bg-red-500/20"
                                                )}
                                            >
                                                {user.watch_expiration && new Date(user.watch_expiration) > new Date() ? (
                                                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> ACTIF</span>
                                                ) : (
                                                    <span className="flex items-center gap-1"><EyeOff className="h-3 w-3" /> INACTIF</span>
                                                )}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded-md">
                                                Label: {user.label_name}
                                            </span>
                                            <span className="text-slate-400 font-medium">
                                                {user.filter_senders_count || 0} expéditeur(s)
                                            </span>
                                        </div>

                                        {/* Actions Bar displayed on hover or if selected */}
                                        <div className={cn("flex items-center gap-2 mt-3 pt-3 border-t border-slate-100", selectedUser?.id === user.id ? "flex" : "hidden group-hover:flex")}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-[10px] w-full border-slate-200 hover:border-primary hover:text-primary"
                                                onClick={(e) => { e.stopPropagation(); handleSetupWatch(user); }}
                                                disabled={isWatchLoading === user.id}
                                            >
                                                {isWatchLoading === user.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                                                Sync
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 ml-auto"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id); }}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Senders List */}
                <div className="col-span-1 lg:col-span-2 space-y-6">
                    {selectedUser ? (
                        <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl min-h-[500px]">
                            <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-slate-50">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <CardTitle className="text-lg font-black uppercase tracking-tight">Liste de Filtrage</CardTitle>
                                        <Badge variant="outline" className="text-xs font-medium border-slate-200 text-slate-500">{selectedUser.email}</Badge>
                                    </div>
                                    <CardDescription>Emails reçus de ces expéditeurs seront déplacés vers '{selectedUser.label_name}'.</CardDescription>
                                </div>
                                <Button size="sm" onClick={() => setIsAddSenderOpen(true)} className="rounded-xl font-bold bg-slate-900 text-white hover:bg-slate-800">
                                    <Plus className="h-4 w-4 mr-2" /> Ajouter
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-slate-50 hover:bg-transparent">
                                            <TableHead className="pl-6 text-[11px] font-black uppercase tracking-widest text-slate-400">Expéditeur</TableHead>
                                            <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Description</TableHead>
                                            <TableHead className="text-right pr-6 w-[100px] text-[11px] font-black uppercase tracking-widest text-slate-400">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filterSenders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="h-64 text-center">
                                                    <div className="flex flex-col items-center justify-center gap-4 text-slate-400">
                                                        <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center">
                                                            <Filter className="h-8 w-8 text-slate-300" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-bold text-slate-500">Liste vide</p>
                                                            <p className="text-xs">Ajoutez des expéditeurs pour commencer le filtrage.</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filterSenders.map((sender) => (
                                                <TableRow key={sender.id} className="group border-slate-50 hover:bg-slate-50/50">
                                                    <TableCell className="pl-6 font-semibold text-slate-700">{sender.sender_email}</TableCell>
                                                    <TableCell className="text-slate-500 text-sm">{sender.description || "-"}</TableCell>
                                                    <TableCell className="text-right pr-6">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                            onClick={() => handleDeleteSender(sender.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center p-12 text-center space-y-4 border-2 border-dashed border-slate-200 rounded-[2.5rem] bg-slate-50/50">
                            <div className="h-16 w-16 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                                <ArrowRight className="h-8 w-8 text-slate-300" />
                            </div>
                            <div className="max-w-xs space-y-2">
                                <h3 className="tex-lg font-bold text-slate-900">Aucun utilisateur sélectionné</h3>
                                <p className="text-sm text-slate-500">Sélectionnez un utilisateur dans la liste de gauche pour configurer ses filtres.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add User Dialog */}
            <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                <DialogContent className="sm:max-w-md border-none shadow-2xl rounded-[2.5rem] bg-white ring-1 ring-slate-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-black uppercase tracking-tight">Ajouter un utilisateur</DialogTitle>
                        <DialogDescription>
                            Configurez un nouvel utilisateur pour le filtrage automatique.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-bold text-xs uppercase tracking-wider text-slate-500">Email Gmail</Label>
                            <Input
                                id="email"
                                placeholder="utilisateur@amc.com"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                className="rounded-xl border-slate-200 focus:ring-primary/20"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="label" className="font-bold text-xs uppercase tracking-wider text-slate-500">Label Gmail (Dossier)</Label>
                            <Input
                                id="label"
                                placeholder="Priority Leads"
                                value={newLabelName}
                                onChange={(e) => setNewLabelName(e.target.value)}
                                className="rounded-xl border-slate-200 focus:ring-primary/20"
                            />
                            <p className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" /> Ce label sera créé automatiquement s'il n'existe pas.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsAddUserOpen(false)} className="rounded-xl">Annuler</Button>
                        <Button onClick={handleAddUser} className="rounded-xl font-bold bg-primary text-white">Ajouter</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Sender Dialog */}
            <Dialog open={isAddSenderOpen} onOpenChange={setIsAddSenderOpen}>
                <DialogContent className="sm:max-w-md border-none shadow-2xl rounded-[2.5rem] bg-white ring-1 ring-slate-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-black uppercase tracking-tight">Ajouter un expéditeur</DialogTitle>
                        <DialogDescription>
                            Les emails de cet expéditeur seront filtrés.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="sender-email" className="font-bold text-xs uppercase tracking-wider text-slate-500">Email Expéditeur</Label>
                            <Input
                                id="sender-email"
                                placeholder="client@exemple.com"
                                value={newSenderEmail}
                                onChange={(e) => setNewSenderEmail(e.target.value)}
                                className="rounded-xl border-slate-200 focus:ring-primary/20"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description" className="font-bold text-xs uppercase tracking-wider text-slate-500">Description (Optionnel)</Label>
                            <Input
                                id="description"
                                placeholder="Client VIP, etc."
                                value={newSenderDescription}
                                onChange={(e) => setNewSenderDescription(e.target.value)}
                                className="rounded-xl border-slate-200 focus:ring-primary/20"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsAddSenderOpen(false)} className="rounded-xl">Annuler</Button>
                        <Button onClick={handleAddSender} className="rounded-xl font-bold bg-primary text-white">Ajouter</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EmailFilterPage;
